import React, {useEffect, useMemo, useState} from 'react';

import {Box} from 'src/design/atoms/Box';
import {VideoProviderTypeEnum} from 'src/api/ProactApiClient';
import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';
import type {IFormData} from 'src/types/Form';
import {LessonForm} from '../courseDefinition/LessonForm';

// timeLimit must only be added when set — an explicit `timeLimit: undefined`
// overrides the LessonForm metadata default (60) instead of falling back to
// it.
function buildLessonFormData(content: CourseDefinitionContentResponse) {
  return {
    ...content.lesson,
    ...(content.timeLimit === undefined ? {} : {timeLimit: content.timeLimit}),
  };
}

export interface EditContentDefinitionLessonProps {
  content: CourseDefinitionContentResponse;
  onContentChange: (content: CourseDefinitionContentResponse) => void;
  /**
   * Renders an extra control below the form given a callback to invoke with
   * a newly-uploaded video id. Used by the Build tab to inject its inline
   * "upload a new video" control without this (frozen `library/` tier) file
   * importing anything from `pages/`.
   */
  renderVideoUploadControl?: (
    onUploaded: (videoId: number) => void
  ) => React.ReactNode;
}
const EditContentDefinitionLesson: React.FC<
  EditContentDefinitionLessonProps
> = ({content, onContentChange, renderVideoUploadControl}) => {
  const [, setIsFormValid] = useState<boolean>(true);
  const [data, setData] = useState(buildLessonFormData(content));

  useEffect(() => {
    setData(buildLessonFormData(content));
  }, [content.lesson, content.timeLimit]);

  const handleFormDataChange = (data: IFormData) => {
    onContentChange({
      ...content,
      timeLimit: data.timeLimit,
      lesson: {
        headerText: data.headerText,
        bodyText: data.bodyText,
        videoProviderType: Number(
          data.videoProviderType
        ) as VideoProviderTypeEnum,
        videoId: data.videoId,
        videoUrl: data.videoUrl,
        enableVideoActiveScreenMonitoring:
          data.enableVideoActiveScreenMonitoring,
        enableVideoAutostart: data.enableVideoAutostart,
        enableVideoControls: data.enableVideoControls,
        enableVideoResume: data.enableVideoResume,
      },
    });
  };

  const handleVideoUploaded = (videoId: number) => {
    const newData = {
      ...data,
      videoProviderType: VideoProviderTypeEnum.Internal,
      videoId,
    };
    setData(newData);
    handleFormDataChange(newData as IFormData);
  };

  return useMemo(
    () => (
      <Box>
        <LessonForm
          data={data}
          mode="edit"
          onFormValidate={setIsFormValid}
          onFormDataChange={handleFormDataChange}
        />
        {renderVideoUploadControl?.(handleVideoUploaded)}
      </Box>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, renderVideoUploadControl]
  );
};

export default EditContentDefinitionLesson;
